// Drawola Studio — "Import a file". Fork-only.
//
// Parsing is entirely upstream's. `loadFromBlob`
// (packages/excalidraw/data/blob.ts) is the same entry point the editor's
// main-menu "Open" uses: it reads the blob, JSON-parses it, rejects anything
// that fails `isValidExcalidrawData`, then runs `restoreElements` (version
// migrations, binding repair, invisible-element pruning) and
// `restoreAppState`. We deliberately re-implement none of that — a file that
// opens in the editor imports into the Studio, and vice versa.
//
// What this module adds is only the two things upstream has no opinion about:
// a file-name → document-name mapping, and trimming the restored AppState down
// to what is safe to persist.

import { clearAppStateForLocalStorage } from "@excalidraw/excalidraw/appState";
import { loadFromBlob } from "@excalidraw/excalidraw/data/blob";

import type { OrderedExcalidrawElement } from "@excalidraw/element/types";
import type { AppState, BinaryFiles } from "@excalidraw/excalidraw/types";

/**
 * `accept` for the file picker: the fork's own extension, upstream's (a
 * `.drawola` file IS an `.excalidraw` file — see ./exportDoc.ts), and plain
 * `.json` because that is what several export paths and OS downloads produce.
 * The MIME entries are hints for platforms that ignore extensions; the real
 * validation is `loadFromBlob`'s, and a user can always force-pick any file.
 */
export const IMPORT_ACCEPT = [
  ".drawola",
  ".excalidraw",
  ".json",
  "application/vnd.drawola+json",
  "application/vnd.excalidraw+json",
  "application/json",
].join(",");

/** Exactly what `createDoc(name, initial)` takes. */
export type ImportedScene = {
  elements: readonly OrderedExcalidrawElement[];
  appState: Partial<AppState>;
  files: BinaryFiles;
};

/** Only the extensions we advertise — an unrelated dot in a name survives. */
const KNOWN_EXTENSION = /\.(drawola|excalidraw|json)$/i;

/**
 * "Sprint plan.drawola" → "Sprint plan".
 *
 * Defensive about paths (some browsers hand back a relative path for a
 * directory pick) and about names that are nothing but an extension.
 */
export const docNameFromFileName = (
  fileName: string,
  fallback: string,
): string => {
  const base = fileName.split(/[\\/]/).pop() ?? fileName;
  const stripped = base.replace(KNOWN_EXTENSION, "").trim();
  return stripped || fallback;
};

/**
 * Read a picked file into a storable scene.
 *
 * Throws whatever `loadFromBlob` throws (`Error: invalid file` for anything
 * that is not a valid Excalidraw scene, including a library file) — the caller
 * turns that into the Studio's error banner.
 */
export const parseImportedFile = async (file: File): Promise<ImportedScene> => {
  // `null` local elements / appState: there is nothing to merge against. The
  // Studio has no editor state, and an import becomes a NEW document rather
  // than overwriting whatever is open behind the overlay.
  const { elements, appState, files } = await loadFromBlob(file, null, null);

  return {
    elements,
    // `restoreAppState` returns a COMPLETE AppState — transient tool state, a
    // `collaborators` Map, a `fileHandle`. Persist only what upstream itself
    // considers browser-storable, which is the same filter `data/LocalData.ts`
    // applies before writing a scene to local storage.
    appState: clearAppStateForLocalStorage(appState),
    files: files ?? {},
  };
};
